/** @jsxImportSource @emotion/react */
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import * as authSelectors from "../../auth/authSelectors";
import { Drawer } from "../../../components/Drawer";
import { ButtonAction } from "../../../components/NewButtons";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { StyledForm } from "../../../components/StyledForm";
import { SpinnerBlock } from "../../common/Spinner";
import { TextInput } from "../../../components/forms/inputs/TextInput";
import { MutateKitchenCategoryPayload } from "../models/kitchen-category";
import {
  useCreateKitchenCategory,
  useGetKitchenCategory,
  useUpdateKitchenCategory,
} from "../service/kitchen-categories-api";
import { useEffect } from "react";
import { css } from "@emotion/react";
import { getErrorMessage, useHandleFetchError } from "../../../error-handler";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  id?: string;
};

const additionalFormStyles = css`
  gap: 16px;
`;

const validationSchema = yup.object({
  name: yup.string().required("Enter name of the kitchen category to continue"),
  title: yup
    .string()
    .required("Enter title of the kitchen category to continue"),
  description: yup
    .string()
    .required("Enter description of the kitchen category to continue")
    .min(3, "Description must be at least 3 characters long"),
});

export const MutateKitchenCategoryDrawer = ({ isOpen, onClose, id }: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const methods = useForm<MutateKitchenCategoryPayload>({
    defaultValues: {
      description: "",
      name: "",
      title: "",
    },
    resolver: yupResolver(validationSchema),
  });

  const { data, isLoading, error } = useGetKitchenCategory(
    organizationKeyName,
    id
  );

  const {
    mutateAsync: createKitchenCategory,
    isLoading: isLoadingCreation,
  } = useCreateKitchenCategory();

  const {
    mutateAsync: updateKitchenCategory,
    isLoading: isLoadingUpdate,
  } = useUpdateKitchenCategory();

  const handleOnClose = () => {
    methods.reset();
    onClose();
  };

  const onSubmit = async (data: MutateKitchenCategoryPayload) => {
    try {
      if (id) {
        await updateKitchenCategory({
          organizationKeyName,
          id,
          data,
        });
      } else {
        await createKitchenCategory({
          organizationKeyName,
          data,
        });
      }
      toastr.success(
        "Success",
        `Kitchen category successfully ${id ? "updated" : "created"}`
      );
      handleOnClose();
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      toastr.error("Error", message);
    }
  };

  useHandleFetchError(error);

  useEffect(() => {
    if (isLoading || !data) {
      return;
    }
    methods.reset({
      name: data.name,
      title: data.title,
      description: data.description,
    });
  }, [isLoading, data, methods]);

  return (
    <Drawer
      title={`${id ? "Edit" : "Create"} kitchen category`}
      width={500}
      buttons={
        <ButtonAction
          disabled={isLoadingCreation || isLoadingUpdate}
          onClick={methods.handleSubmit(onSubmit)}
          type="button"
        >
          {isLoadingCreation || isLoadingUpdate ? <ActionSpinner /> : "Save"}
        </ButtonAction>
      }
      open={isOpen}
      onClose={handleOnClose}
    >
      <FormProvider {...methods}>
        {isLoading ? (
          <SpinnerBlock />
        ) : (
          <StyledForm additionalStyles={additionalFormStyles}>
            <TextInput
              label="Kitchen category name"
              name="name"
              helperText="Only visible to admins."
            />
            <TextInput
              label="Kitchen category title"
              name="title"
              helperText="Visible for users."
            />
            <TextInput
              label="Kitchen category description"
              name="description"
              helperText="Description of the kitchen category"
            />
          </StyledForm>
        )}
      </FormProvider>
    </Drawer>
  );
};
